import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';

import { HoldReceiptSubmitEvent } from '../../models/hold-receipt.model';

/**
 * HoldReceiptDialogComponent
 *
 * Standalone child component responsible for:
 *  - Rendering the Hold Receipt modal dialog
 *  - Validating mobile number + customer name
 *  - Trapping Tab / Enter keyboard focus within the dialog
 *  - Emitting typed events to the parent (Dashboard)
 *
 * The component owns ZERO business logic — it is a pure UI shell.
 * All order-placement logic remains in DashboardComponent.saveReceipt().
 *
 * Replaces the inline <p-dialog> block at L1388–1480 of
 * dashboard.component.html.
 */
@Component({
  selector: 'app-hold-receipt-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, ButtonModule],
  templateUrl: './hold-receipt-dialog.component.html',
  styleUrl: './hold-receipt-dialog.component.scss',
})
export class HoldReceiptDialogComponent implements OnChanges, AfterViewInit {
  // ── Inputs ──────────────────────────────────────────────────────────────

  /** Controls dialog visibility (two-way bindable). */
  @Input() visible: boolean = false;

  /** Pre-populated from the order header's mobile number field. */
  @Input() mobileNumber: string = '';

  /** The customer name the cashier enters before holding. */
  @Input() holdReceiptName: string = '';

  /** When true, a valid 10-digit mobile is required to submit. */
  @Input() isMobileMandatory: boolean = false;

  // ── Outputs ─────────────────────────────────────────────────────────────

  /** Two-way binding support for [(visible)]. */
  @Output() visibleChange = new EventEmitter<boolean>();

  /**
   * Emitted when the cashier clicks "Save Receipt" with valid data.
   * The Dashboard updates its state and calls saveReceipt(true).
   */
  @Output() submitted = new EventEmitter<HoldReceiptSubmitEvent>();

  /** Emitted when the cashier clicks "Cancel" or the close button. */
  @Output() cancelled = new EventEmitter<void>();

  // ── ViewChild references ─────────────────────────────────────────────────

  @ViewChild('holdMobileInput') holdMobileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('holdNameInput') holdNameInputRef!: ElementRef<HTMLInputElement>;

  // ── Internal state ───────────────────────────────────────────────────────

  /** Local copy — mutated by the dialog, emitted on submit. */
  localMobileNumber: string = '';

  /** Local copy — mutated by the dialog, emitted on submit. */
  localHoldReceiptName: string = '';

  // ── Lifecycle ────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    // Mobile number always syncs from parent (driven by the order header)
    if (changes['mobileNumber']) {
      this.localMobileNumber = this.mobileNumber;
    }

    if (changes['visible']) {
      if (this.visible) {
        // Dialog is OPENING — sync holdReceiptName from parent and auto-focus
        this.localHoldReceiptName = this.holdReceiptName;
        setTimeout(() => this.focusFirstRequiredInput(), 150);
      } else {
        // Dialog is CLOSING — clear local fields so they are blank on next open
        this.localHoldReceiptName = '';
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.visible) {
      setTimeout(() => this.focusFirstRequiredInput(), 150);
    }
  }

  // ── Computed helpers ─────────────────────────────────────────────────────

  /** True when mobile is required but not yet valid. */
  get isMobileInvalid(): boolean {
    return (
      this.isMobileMandatory &&
      (!this.localMobileNumber || this.localMobileNumber.length !== 10)
    );
  }

  /** Whether the mobile input should be shown (not yet filled from header). */
  get showMobileInput(): boolean {
    return !this.mobileNumber || this.mobileNumber.length !== 10;
  }

  /** Whether the Submit button should be disabled. */
  get isSubmitDisabled(): boolean {
    return !this.localHoldReceiptName || this.isMobileInvalid;
  }

  // ── Event handlers ───────────────────────────────────────────────────────

  onSubmit(): void {
    if (this.isSubmitDisabled) return;

    // Capture values before resetting
    const payload = {
      mobileNumber: this.localMobileNumber,
      holdReceiptName: this.localHoldReceiptName,
    };

    // Close dialog and clear local fields IMMEDIATELY — prevents the
    // "customer name flash" where the dialog stays visible while the parent
    // is processing saveReceipt(true).
    this.visibleChange.emit(false);
    this.localHoldReceiptName = '';
    this.localMobileNumber = '';

    // Emit to parent AFTER dialog is already closing
    this.submitted.emit(payload);
  }

  onCancel(): void {
    this.closeDialog();
    this.cancelled.emit();
  }

  closeDialog(): void {
    this.visibleChange.emit(false);
  }

  /** Allow only numeric keypresses in the mobile field. */
  allowOnlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.charCode ?? event.keyCode;
    return charCode >= 48 && charCode <= 57;
  }

  /**
   * Tab / Enter keyboard trap inside the dialog.
   * Mirrors handleHoldDialogTabNavigation() from dashboard.component.ts.
   */
  onKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Tab' && event.key !== 'Enter') return;

    const mobileInput = this.holdMobileInputRef?.nativeElement;
    const nameInput = this.holdNameInputRef?.nativeElement;

    // Tab from mobile → name
    if (mobileInput && document.activeElement === mobileInput) {
      if (event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        setTimeout(() => nameInput?.focus(), 0);
        return;
      }
    }

    // Tab / Enter from name → Submit button
    if (nameInput && document.activeElement === nameInput) {
      event.preventDefault();
      event.stopPropagation();
      setTimeout(() => {
        const submitBtn = document.querySelector(
          '.hold-dialog-submit-btn'
        ) as HTMLButtonElement | null;
        if (submitBtn && !submitBtn.disabled) {
          submitBtn.focus();
          submitBtn.style.outline = '2px solid #2A5EDE';
          submitBtn.addEventListener(
            'blur',
            () => (submitBtn.style.outline = ''),
            { once: true }
          );
        }
      }, 50);
    }
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  private focusFirstRequiredInput(): void {
    const mobileInput = this.holdMobileInputRef?.nativeElement;
    const nameInput = this.holdNameInputRef?.nativeElement;

    if (this.showMobileInput && mobileInput) {
      mobileInput.focus();
      mobileInput.select();
    } else if (nameInput) {
      nameInput.focus();
      nameInput.select();
    }
  }
}
